import { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  IPartProTradeBidVehicleDetail,
  IRejectPartProTradeVehicle,
  PartProTradeRejectReason,
} from "../../models/partPro/IPartProTradeBids";
import { normaliseCancelNote } from "../../utils/partProTradeCancel";
import {
  awardsCancelledByReject,
  isValidReject,
  PARTPRO_TRADE_REJECT_NOTE_MAX_LENGTH,
  partProTradeRejectReasonLabels,
} from "../../utils/partProTradeReject";
import PriceFormat from "../../utils/priceFormat";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

interface RejectTradeVehicleModalProps {
  /** The trade vehicle being rejected; null closes the modal */
  vehicle: IPartProTradeBidVehicleDetail | null;
  onClose: () => void;
  isLoading: boolean;
  onConfirm: (request: IRejectPartProTradeVehicle) => void;
}

interface FormFields {
  reason?: PartProTradeRejectReason;
  note: string;
}

// Rejecting the whole car cancels every accepted bid on it, and the buyers are told why, so it
// always needs a reason. Shared by the pricing matrix and the Bids page.
export default function RejectTradeVehicleModal({ vehicle, onClose, isLoading, onConfirm }: RejectTradeVehicleModalProps) {
  const { t } = useTranslation("vehicles");

  const { register, setValue, watch, reset } = useForm<FormFields>({
    // No default: the buyers are told the reason, so it has to be a choice someone made
    defaultValues: { reason: undefined, note: "" },
  });

  useEffect(() => {
    if (!vehicle) reset({ reason: undefined, note: "" });
  }, [vehicle, reset]);

  const reason = watch("reason");
  const note = watch("note");
  const valid = isValidReject(reason, note);

  const reasonOptions = (Object.values(PartProTradeRejectReason) as PartProTradeRejectReason[])
    .filter((value): value is PartProTradeRejectReason => typeof value === "number")
    .map((value) => ({ id: value, name: t(partProTradeRejectReasonLabels[value]) }));

  const awarded = awardsCancelledByReject(vehicle);

  return (
    <ConfirmModal
      open={!!vehicle}
      setOpen={(open: boolean) => !open && onClose()}
      onConfirm={() => {
        if (reason === undefined || !isValidReject(reason, note)) return;
        onConfirm({ reason, note: normaliseCancelNote(note) });
      }}
      isLoading={isLoading}
      confirmDisabled={!valid}
      title={t("Reject {{vehicle}} on PartPro Trade?", { vehicle: vehicle?.vrm ?? vehicle?.name ?? "" })}
      message={t(
        "Every accepted bid on this vehicle is cancelled and its price cleared from the matrix, and the other bids are voided. The buyers are told your reason in their daily digest. If the collection is rescheduled, the vehicle is listed again for fresh bids. This can't be undone."
      )}
      confirmButtonText={t("Reject vehicle")}
    >
      <div className="mt-4 flex flex-col gap-y-3">
        {awarded.length > 0 && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <p className="font-medium">{t("These accepted bids will be cancelled:")}</p>
            <ul className="mt-1 list-disc pl-5">
              {awarded.map((lot) => (
                <li key={lot.id}>
                  {lot.categoryName} · {lot.award!.accountName ?? lot.award!.buyerCompanyName} ·{" "}
                  {PriceFormat(lot.award!.amount)}
                </li>
              ))}
            </ul>
          </div>
        )}

        <SelectInput
          label={t("Reason")}
          register={register}
          registerName="reason"
          options={reasonOptions}
          setValue={setValue}
          watch={watch}
          cols="w-full"
          placeholder={t("Select a reason")}
          rounded
        />

        <TextInput
          label={
            reason === PartProTradeRejectReason.Other ? t("Note for the buyers") : t("Note for the buyers (optional)")
          }
          register={register}
          registerName="note"
          richText
          rows={3}
          cols="w-full"
          maxLength={PARTPRO_TRADE_REJECT_NOTE_MAX_LENGTH}
        />
      </div>
    </ConfirmModal>
  );
}
